import { ViewLayer } from './view-settings';

export type LayerLoadingState = 'loading' | 'done' | 'failed';

export type LayerLoadingStatus = {
  id: number;
  layer: ViewLayer;
  state: LayerLoadingState;
};

/**
 * The timeline layers that are initializing, or have just finished or failed to: the orthophoto,
 * the tiled model and the point cloud each initialize on their own, once SkyView is shown. Viewer
 * code reports them, React shows them and removes them once they are done with.
 */
export class LayerLoadingStatuses {
  private static _instance: LayerLoadingStatuses | undefined;

  static get instance() {
    if (!LayerLoadingStatuses._instance) {
      LayerLoadingStatuses._instance = new LayerLoadingStatuses();
    }
    return LayerLoadingStatuses._instance;
  }

  private statuses: LayerLoadingStatus[] = [];
  private nextId = 1;
  private readonly listeners = new Set<() => void>();

  getSnapshot = (): LayerLoadingStatus[] => this.statuses;

  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /** Reports the layer as initializing, returning the id of its status. */
  start(layer: ViewLayer) {
    const id = this.nextId++;
    this.update([...this.statuses, { id, layer, state: 'loading' }]);
    return id;
  }

  finish(id: number) {
    this.setState(id, 'done');
  }

  fail(id: number) {
    this.setState(id, 'failed');
  }

  /** Removes the status, e.g. once it has faded out or a failed one is clicked. */
  dismiss(id: number) {
    this.update(this.statuses.filter((status) => status.id !== id));
  }

  clear() {
    this.update([]);
  }

  /**
   * Initializes the layer, reporting its status. A failure is reported and logged, not thrown, so
   * the other layers go on initializing. Once the signal is aborted, as when another timeline is
   * shown, the status is removed instead.
   *
   * @returns Whether the layer initialized.
   */
  async track(layer: ViewLayer, initialize: () => Promise<void>, signal?: AbortSignal) {
    const id = this.start(layer);
    try {
      await initialize();
      signal?.throwIfAborted();
      this.finish(id);
      return true;
    } catch (err) {
      if (signal?.aborted) {
        this.dismiss(id);
        return false;
      }
      console.error(err);
      this.fail(id);
      return false;
    }
  }

  private setState(id: number, state: LayerLoadingState) {
    this.update(this.statuses.map((status) => (status.id === id ? { ...status, state } : status)));
  }

  private update(statuses: LayerLoadingStatus[]) {
    this.statuses = statuses;
    for (const listener of this.listeners) {
      listener();
    }
  }
}
